import type { UseMutationResult } from "@tanstack/react-query"
import { useMutation, useQueryClient } from "@tanstack/react-query"

import { queryKeys } from "~/api/cache"

import {
  invalidateBatteryPrice,
  invalidateBatteryPriceDependents,
} from "./battery-price-dependents"
import type { BatteryProposalPricingDto } from "../dto/battery-proposal.dto"
import { batteryProposalService } from "../services/battery-proposal.service"

export type CalculateBatteryProposalPricingVariables = {
  proposalId: string
}

export default function useCalculateBatteryProposalPricing(): UseMutationResult<
  BatteryProposalPricingDto,
  unknown,
  CalculateBatteryProposalPricingVariables
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ proposalId }: CalculateBatteryProposalPricingVariables) =>
      batteryProposalService.postPricing(proposalId),
    onSuccess: (pricing, { proposalId }) => {
      queryClient.setQueryData(
        queryKeys.batteryProposal.pricing(proposalId),
        pricing,
      )
      invalidateBatteryPriceDependents(queryClient, proposalId)
    },
    // Refresh marks a retained Quote stale before it calls the provider, so a
    // provider or persistence failure still moved server state. Refreshing the
    // same set as success keeps a Quote the server now calls stale from being
    // presented as current.
    onError: (_error, { proposalId }) => {
      invalidateBatteryPrice(queryClient, proposalId)
    },
  })
}
